import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { restoreItem } from '../lib/db'
import { Banner, Button, Field, Row, Segmented, Sheet, Tag, type SwipeAction, useToast } from '../ui'
import { useAddItem, useRemoveItem, useSetItemFields, useUpdateItem } from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { useFeed } from '../lib/queries'
import { addPurchase } from '../lib/listAdd'
import { heroIsVenue, heroUrl, posterUrl } from '../lib/entries'
import { leadingOf, metaLine, plateFor, rowTitle } from '../lib/rowMeta'
import { purchaseStatusOf, shopOf, totalsByCurrency } from '../lib/listSummary'
import { priceSymbol } from '../lib/currency'
import { useLinger } from '../lib/useLinger'
import { Check } from 'lucide-react'
import { EventSheet } from './EventSheet'
import { RowMore } from './RowMore'
import type { FeedRow, List, ListItem, PurchaseStatus } from '../lib/types'
import './EventRow.css'

const STATUS_OPTIONS: { value: PurchaseStatus; label: string }[] = [
  { value: 'considering', label: 'Considering' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'bought', label: 'Bought' },
]

/** An item written before this package ran, or one added by the paste path
 *  below, carries no `status` at all — the guard only forbids the column on
 *  a non-`buy` list, it never requires it on one. Treating a null the same
 *  as `'considering'` everywhere this file groups or counts means the paste
 *  path needs no follow-up write to set a default. The rule lives in
 *  `listSummary.ts` so the Lists index counts the same rows this screen
 *  draws (UI-audit a2-01). */
const statusOf = purchaseStatusOf

// `priceSymbol` knows the two regions' currencies; pounds were here first and
// stay, and any other code is spelled out with a space after it.
function formatPrice(cents: number, currency: string): string {
  const s = currency === 'GBP' ? '£' : priceSymbol(currency)
  return `${s === currency ? `${s} ` : s}${(cents / 100).toFixed(2)}`
}


/**
 * L6: purchases. **Doug answered the `robots.txt` question 27 Sept: yes** —
 * a page pasted here is the user's own fetch, not a crawl, and different in
 * kind from rule 4's recipes that walk a site on a schedule. The paste path
 * is now whole: `add` inserts the row with the pasted text as its body, and
 * `fillFromPreview` calls the Worker's `GET /link-preview` (`src/linkPreview.ts`,
 * pure and tested; `src/inbox.ts` owns the one fetch) and fills in a real
 * title, price and picture once it answers. A 403, or a page with nothing to
 * read, leaves the row exactly as pasted — never retried, never with a
 * different user agent (rule 4).
 */
export function PurchaseList({ list, items }: { list: List; items: ListItem[] }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [segment, setSegment] = useState<PurchaseStatus>('considering')
  const [draft, setDraft] = useState('')
  const [edit, setEdit] = useState<ListItem | null>(null)
  // L8: which event's sheet is open, from tapping an item that carries an
  // `entry_id` — the item editor is not reached from that tap.
  const [openEntry, setOpenEntry] = useState<FeedRow | null>(null)

  const addM = useAddItem()
  const removeM = useRemoveItem()
  const updateM = useUpdateItem()
  const fieldsM = useSetItemFields()
  const error = useWriteError(addM, removeM, updateM, fieldsM)
  const busy = addM.isPending

  const feedQ = useFeed()
  const feedById = useMemo(() => new Map((feedQ.data ?? []).map(e => [e.id, e])), [feedQ.data])
  const entryFor = (i: ListItem): FeedRow | null => (i.entry_id ? feedById.get(i.entry_id) ?? null : null)

  // The after-checked state (`lib/finished.ts`, `useLinger`): a row marked
  // ordered or bought stays in the segment it is leaving, struck through,
  // for the linger, then collapses out of it. `wasIn` remembers that segment
  // — the write has already moved the row's status on.
  const linger = useLinger()
  const wasIn = useRef(new Map<string, PurchaseStatus>())
  useEffect(() => { if (fieldsM.isError) linger.cancelAll() }, [fieldsM.isError, linger])
  const counts: Record<PurchaseStatus, number> = { considering: 0, ordered: 0, bought: 0 }
  for (const i of items) counts[statusOf(i)]++
  const visible = items.filter(i => linger.lingering.has(i.id) ? wasIn.current.get(i.id) === segment : statusOf(i) === segment)
  const totals = totalsByCurrency(visible)
  const flush = !visible.some(i => i.image_url)

  const options = STATUS_OPTIONS.map(o => ({ value: o.value, label: `${o.label} · ${counts[o.value]}` }))

  async function add(text: string) {
    const out = await addPurchase({ addM, updateM, fieldsM }, list, items, text)
    if (out.ok && !out.empty) setDraft('')
  }

  // F7: removal is a swipe and a toast, never a confirmation dialog — the same
  // shape `ListDetail.tsx`'s own `restore` is, with this kind's own columns.
  async function restore(i: ListItem, gone: Promise<unknown>) {
    const { error: err } = await restoreItem(i, gone)
    if (!err) void qc.invalidateQueries({ queryKey: ['list_items'] })
  }

  const del = (i: ListItem) => {
    // The delete's own promise, so Undo writes the row back after it and
    // not before (a2-14): the row returns under its own id now, which a
    // delete still in flight would otherwise remove a second time.
    const gone = removeM.mutateAsync(i.id).catch(() => {})
    toast({ text: `Removed "${i.body}"`, action: { label: 'Undo', onClick: () => void restore(i, gone) } })
  }

  /** Rule 2: every status change has Undo. `next` is read off `statusOf`, not
   *  `i.status`, so a null-status row (never explicitly written) advances the
   *  same way an explicit `'considering'` one does. */
  function advance(i: ListItem, next: PurchaseStatus) {
    const prevStatus = i.status
    wasIn.current.set(i.id, statusOf(i))
    fieldsM.mutate({ id: i.id, patch: { status: next } })
    linger.start(i.id)
    toast({
      text: next === 'bought' ? `Marked "${i.body}" bought` : `Marked "${i.body}" ordered`,
      action: { label: 'Undo', onClick: () => { linger.cancel(i.id); fieldsM.mutate({ id: i.id, patch: { status: prevStatus } }) } },
    })
  }

  return (
    <>
      {error && <Banner tone="bad">{error}</Banner>}

      <div className="lists-segscroll">
        <Segmented className="u-seg--fit" value={segment} onChange={v => setSegment(v as PurchaseStatus)} options={options} />
      </div>
      {totals.length > 0 && <p className="buy__total">~{totals.map(t => formatPrice(t.cents, t.currency)).join(' + ')}</p>}

      <div className="lists-addrow">
        <Field label="Add" className="lists-addrow__field">
          <Field.Input
            // No inputMode="url": iOS's URL keyboard has no space bar, and this
            // field takes a typed name ("Pepper grinder") as often as a link.
            value={draft} placeholder="Paste a link, or type…" autoComplete="off"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void add(draft) }}
          />
        </Field>
        <Button variant="primary" disabled={busy || !draft.trim()} onClick={() => void add(draft)}>Add</Button>
      </div>

      {!visible.length ? (
        <div className="stub">Nothing {segment === 'considering' ? 'here' : segment} yet.</div>
      ) : (
        <div className="lists-group">
          {visible.map(i => {
            const status = statusOf(i)
            const next: PurchaseStatus | null = status === 'considering' ? 'ordered' : status === 'ordered' ? 'bought' : null
            return (
              <PurchaseRow
                key={i.id} item={i} entry={entryFor(i)} flush={flush} onOpenEvent={setOpenEntry}
                done={linger.lingering.has(i.id)} leaving={linger.leaving.has(i.id)}
                onAdvance={next ? () => advance(i, next) : undefined}
                onDelete={() => del(i)}
                onOpen={() => setEdit(i)}
              />
            )
          })}
        </div>
      )}

      {edit && (
        <PurchaseEditSheet
          item={edit}
          onClose={() => setEdit(null)}
          onSave={patch => {
            const id = edit.id
            setEdit(null)
            updateM.mutate({ id, patch: { body: patch.body, link: patch.link } })
            fieldsM.mutate({ id, patch: { price_cents: patch.price_cents, status: patch.status, note: patch.note } })
          }}
        />
      )}

      <EventSheet e={openEntry} onClose={() => setOpenEntry(null)} />
    </>
  )
}

function PurchaseRow({ item, entry, flush, onOpenEvent, done, leaving, onAdvance, onDelete, onOpen }: {
  item: ListItem
  /** L8: the feed row this item was added from, or null once it has left the
   *  feed — see `TaskList.tsx`'s own note on the same field. */
  entry?: FeedRow | null
  /** No row in this segment has a picture, so none keeps an empty leading
   *  slot for one (`u-row--flush`). Decided per segment, not per row: a row
   *  without a picture among rows with one keeps the slot, so the titles
   *  stay in one column. */
  flush: boolean
  onOpenEvent?: (e: FeedRow) => void
  /** The linger: struck through where it sits, then collapsing out. */
  done?: boolean; leaving?: boolean
  onAdvance?: () => void; onDelete: () => void; onOpen: () => void
}) {
  // A lingering row is drawn as the segment it is leaving still saw it: the
  // status the write moved on is not the one the row's controls answer to.
  const status = done ? (statusOf(item) === 'bought' ? 'ordered' : 'considering') : statusOf(item)
  const advanceLabel = status === 'considering' ? 'Mark ordered' : 'Mark bought'
  const swipe = {
    left: { label: 'Remove', onAction: onDelete, leaves: true } as SwipeAction,
    ...(onAdvance ? { right: { label: advanceLabel, onAction: onAdvance } as SwipeAction } : {}),
  }

  // a2-21, Doug chose: the shop and the price on every row, and "Mark
  // ordered" as a button on a row still being considered. The grey "Link"
  // chip that led every row is gone — fifteen identical chips said nothing.
  // The shop is the link's hostname (`www.` dropped); both sit on the meta
  // line rather than the trailing slot, which at 360px has room for the
  // button and the "…" and not for a price as well. The status is not
  // repeated per row: it is the segment you are looking at.
  const host = shopOf(item.link)
  const price = item.price_cents != null ? formatPrice(item.price_cents, item.currency) : null
  const words = [host, item.note].filter(Boolean).join(' · ')
  const facts = (
    <>
      {words}
      {price && <>{words ? ' · ' : ''}<span className="buy-row__price">{price}</span></>}
    </>
  )

  const trailing = (
    <>
      {status === 'considering' && onAdvance && (
        <Button size="sm" variant="quiet" className="buy-row__advance" onClick={onAdvance}>Mark ordered</Button>
      )}
      <RowMore
        name={item.body} onEdit={onOpen} onRemove={onDelete}
        extra={status === 'ordered' && onAdvance
          ? [{ label: 'Mark bought', icon: <Check size={20} aria-hidden="true" />, onSelect: onAdvance }]
          : []}
      />
    </>
  )
  const leading = item.image_url
    ? <img src={item.image_url} alt="" width={44} height={44} loading="lazy" decoding="async" />
    : null
  const cls = `u-row--list${flush ? ' u-row--flush' : ''}`

  // L8: see `TaskList.tsx`'s own note on the same shape.
  if (item.entry_id) {
    if (entry) return <EventItemRow entry={entry} trailing={trailing} swipe={swipe} onOpen={() => onOpenEvent?.(entry)} />
    return (
      <Row
        className={cls}
        leaving={leaving}
        leading={leading}
        title={<span className={`lists-itembody${done ? ' is-done' : ''}`}>{item.body}</span>}
        meta={<Tag>Passed</Tag>}
        trailing={trailing}
        swipe={swipe}
      />
    )
  }

  return (
    <Row
      className={cls}
      leaving={leaving}
      leading={leading}
      title={
        <button type="button" className={`lists-itembody${done ? ' is-done' : ''}`} onClick={onOpen}>{item.body}</button>
      }
      meta={words || price ? facts : null}
      trailing={trailing}
      swipe={swipe}
    />
  )
}

function PurchaseEditSheet({ item, onClose, onSave }: {
  item: ListItem
  onClose: () => void
  onSave: (patch: {
    body: string; link: string | null; price_cents: number | null
    status: PurchaseStatus; note: string | null
  }) => void
}) {
  const [body, setBody] = useState(item.body)
  const [link, setLink] = useState(item.link ?? '')
  const [price, setPrice] = useState(item.price_cents != null ? (item.price_cents / 100).toFixed(2) : '')
  const [status, setStatus] = useState<PurchaseStatus>(statusOf(item))
  const [note, setNote] = useState(item.note ?? '')

  function save() {
    const trimmedPrice = price.trim().replace(',', '.')
    const cents = trimmedPrice ? Math.round(parseFloat(trimmedPrice) * 100) : null
    onSave({
      body: body.trim(),
      link: link.trim() || null,
      price_cents: cents != null && Number.isFinite(cents) ? cents : null,
      status,
      note: note.trim() || null,
    })
  }

  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Edit purchase"
      actions={
        <>
          <Button variant="primary" disabled={!body.trim()} onClick={save}>Save</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <div className="lists-form">
        <Field label="Item"><Field.Input value={body} autoFocus onChange={e => setBody(e.target.value)} /></Field>
        <Field label="Link">
          <Field.Input value={link} placeholder="https://…" inputMode="url" onChange={e => setLink(e.target.value)} />
        </Field>
        <Field label="Price">
          <Field.Input value={price} placeholder="0.00" inputMode="decimal" onChange={e => setPrice(e.target.value)} />
        </Field>
        <Field label="Status">
          <div className="lists-segscroll">
            <Segmented value={status} onChange={v => setStatus(v as PurchaseStatus)} options={STATUS_OPTIONS} />
          </div>
        </Field>
        <Field label="Note"><Field.Input value={note} onChange={e => setNote(e.target.value)} /></Field>
      </div>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// L8: an event, inside a list — the same shape `TaskList.tsx`'s own copy is,
// for the same reason (`Thumb`'s own note there).
// ---------------------------------------------------------------------------

function Thumb({ e }: { e: FeedRow }) {
  const now = new Date()
  const kind = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  return (
    <span className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as { [k: string]: string }) : undefined}>
      {plate && (
        <span className="ev-plate">
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img src={kind.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
      )}
    </span>
  )
}

function EventItemRow({ entry, trailing, swipe, onOpen }: {
  entry: FeedRow
  trailing: ReactNode
  swipe?: { left?: SwipeAction; right?: SwipeAction }
  onOpen: () => void
}) {
  const now = new Date()
  const m = metaLine(entry, { now })
  const rest = [m.venue, m.fact].filter(Boolean).join(' · ')
  return (
    <Row
      leading={<Thumb e={entry} />}
      title={rowTitle(entry)}
      meta={
        <>
          <span className="ev-row__dot" style={{ background: m.dot }} aria-hidden="true" />
          <b className="ev-row__when">{m.when}</b>
          {rest && <span className="ev-row__rest">{rest}</span>}
        </>
      }
      trailing={trailing}
      onPress={onOpen}
      swipe={swipe}
    />
  )
}
